import * as React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, Skeleton, ShieldLine } from '@teivor/shared-ui';

import { useMeQuery } from '../features/settings/queries';

/**
 * Route-level role gate (UX only — the backend @Roles guards remain the
 * authority; this just avoids dead-end 403 pages for deep links). While the
 * role is loading it shows a skeleton; on mismatch a polite no-access state.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: string[];
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const me = useMeQuery();

  if (me.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const role = me.data?.role;
  if (!role || !roles.includes(role)) {
    return (
      <EmptyState
        icon={<ShieldLine size={40} aria-hidden />}
        title={t('errors.no_access_title', 'You do not have access to this area')}
        description={t(
          'errors.no_access_description',
          'This section is restricted. Ask an administrator if you believe you should have access.',
        )}
        action={
          <Button variant="secondary" asChild>
            <Link to="/">{t('errors.go_home', 'Go home')}</Link>
          </Button>
        }
      />
    );
  }

  return <>{children}</>;
}
